import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Target, Sparkles } from "lucide-react";

const DEPT_NORMALIZE = { "Koebenhavn": "København" };
const displayName = (name) => DEPT_NORMALIZE[name] || name;

// Per-weekday mål og breakeven pr. afdeling
// Indeks: 0=søndag, 1=mandag, 2=tirsdag, 3=onsdag, 4=torsdag, 5=fredag, 6=lørdag
// Format: [breakeven, lav_sæson, høj_sæson]
const WEEKDAY_GOALS = {
  Koebenhavn: {
    1: [13000, 15000, 18050], // Mandag
    2: [19000, 21000, 25650], // Tirsdag
    3: [19000, 21000, 25650], // Onsdag
    4: [19000, 21000, 25650], // Torsdag
    5: [14000, 16000, 19000], // Fredag
    6: [10000, 12000, 14250], // Lørdag
  },
  Kolding: {
    1: [17000, 19000, 23750],
    2: [11000, 13000, 15200],
    3: [17000, 19000, 23750],
    4: [11000, 13000, 15200],
    5: [11000, 13000, 15200],
  },
  Odense: {
    1: [14000, 16000, 19000],
    2: [10000, 12000, 13300],
    3: [14000, 16000, 19000],
    4: [10000, 12000, 13300],
    5: [10000, 12000, 13300],
  },
  Aalborg: {
    1: [8000, 10000, 11400],
    2: [14000, 16000, 18050],
    3: [8000, 10000, 11400],
    4: [11000, 13000, 14250],
    5: [11000, 13000, 14250],
  },
  Aarhus: {
    1: [13000, 15000, 18050],
    2: [14000, 16000, 19000],
    3: [14000, 16000, 19000],
    4: [14000, 16000, 19000],
    5: [10000, 12000, 13300],
    6: [10000, 12000, 13300],
  },
};

const HIGH_SEASON_MONTHS = [4, 5, 6, 7, 8];
const WORK_DAYS_PER_WEEK = 5;
const WORK_DAYS_PER_MONTH = 22;


function getSeason() {
  const month = new Date().getMonth() + 1;
  return HIGH_SEASON_MONTHS.includes(month) ? "high" : "low";
}

function getTodayWeekday() {
  return new Date().getDay(); // 0=søn, 1=man, ..., 6=lør
}

// København gradvis optrapning april–juni 2026
// Fase 1: nu–19. apr  → mål 12.500, breakeven 10.000
// Fase 2: 20. apr–26. apr → mål 14.500, breakeven 12.000
// Fase 3: 27. apr–10. maj → mål 16.500, breakeven 14.000
// Fase 4: 11. maj–12. jun → mål 18.500, breakeven 16.000
// Fra 13. juni: normale tal
// Brug YYYY-MM-DD strenge og sammenlign som strings — undgår UTC/lokal tidszone-problemer
const KBH_PHASES = [
  { from: "2026-06-13", goal: null, breakEven: null },   // normale tal
  { from: "2026-05-11", goal: 18500, breakEven: 16000 },
  { from: "2026-04-27", goal: 16500, breakEven: 14000 },
  { from: "2026-04-20", goal: 14500, breakEven: 12000 },
  { from: "2026-04-13", goal: 12500, breakEven: 10000 },
];

function getKbhPhase() {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  for (const phase of KBH_PHASES) {
    if (today >= phase.from) return phase;
  }
  return null;
}

// Hent mål/breakeven for en given ugedag (0=søn..6=lør)
// weekday parameter: JS getDay() værdi. Default = i dag.
function getWeekdayData(dept, weekday) {
  if (weekday === undefined) weekday = new Date().getDay();
  const deptData = WEEKDAY_GOALS[dept];
  if (!deptData || !deptData[weekday]) return null;
  return deptData[weekday]; // [breakeven, low, high]
}

// Gennemsnit af alle hverdage (man-fre) — bruges til uge/måned-beregninger
function getAvgWeekday(dept) {
  const deptData = WEEKDAY_GOALS[dept];
  if (!deptData) return [0, 0, 0];
  let sumBE = 0, sumLow = 0, sumHigh = 0, count = 0;
  for (let wd = 1; wd <= 5; wd++) {
    const d = deptData[wd];
    if (d) { sumBE += d[0]; sumLow += d[1]; sumHigh += d[2]; count++; }
  }
  if (count === 0) return [0, 0, 0];
  return [Math.round(sumBE / count), Math.round(sumLow / count), Math.round(sumHigh / count)];
}

function getGoalDay(dept, weekday) {
  if (weekday === undefined) weekday = new Date().getDay();
  // København optrapning — overskriver normale tal
  if (dept === "Koebenhavn") {
    const phase = getKbhPhase();
    if (phase && phase.goal !== null) {
      // Under optrapning: brug fase-mål for hverdage, skaler for lørdag
      if (weekday === 6) {
        const satData = getWeekdayData(dept, 6);
        const avgData = getAvgWeekday(dept);
        if (satData && avgData[1] > 0) return Math.round(satData[1] * (phase.goal / avgData[1]));
        return phase.goal;
      }
      return phase.goal;
    }
  }
  const data = getWeekdayData(dept, weekday);
  if (!data) {
    // Fallback: brug gennemsnit (burde ikke ske)
    const avg = getAvgWeekday(dept);
    return getSeason() === "high" ? avg[2] : avg[1];
  }
  return getSeason() === "high" ? data[2] : data[1];
}

function getBreakEvenDay(dept, weekday) {
  if (weekday === undefined) weekday = new Date().getDay();
  // København optrapning
  if (dept === "Koebenhavn") {
    const phase = getKbhPhase();
    if (phase && phase.breakEven !== null) {
      if (weekday === 6) {
        const satData = getWeekdayData(dept, 6);
        const avgData = getAvgWeekday(dept);
        if (satData && avgData[0] > 0) return Math.round(satData[0] * (phase.breakEven / avgData[0]));
        return phase.breakEven;
      }
      return phase.breakEven;
    }
  }
  const data = getWeekdayData(dept, weekday);
  if (!data) {
    const avg = getAvgWeekday(dept);
    return avg[0];
  }
  return data[0];
}

const ALL_DEPTS = ["Kolding", "Odense", "Aarhus", "Aalborg", "Koebenhavn"];

const formatDKK = (amount) =>
  new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// --- Tidsbaseret progression ---

function getDayProgress() {
  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;
  // Arbejdsdag 08:00–17:00
  return Math.min(Math.max((hour - 8) / (17 - 8), 0), 1);
}

function getWeekProgress() {
  const now = new Date();
  const day = now.getDay(); // 0=sun,1=mon,...,6=sat
  const hour = now.getHours() + now.getMinutes() / 60;
  const workdayFraction = Math.min(Math.max((hour - 8) / (17 - 8), 0), 1);
  const workDayIndex = day === 0 || day === 6 ? 5 : day; // weekend = fredag
  const elapsed = (workDayIndex - 1) + workdayFraction;
  return Math.min(elapsed / WORK_DAYS_PER_WEEK, 1);
}

function getMonthProgress() {
  const now = new Date();
  const totalDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const hour = now.getHours() + now.getMinutes() / 60;
  const dayFraction = Math.min(Math.max((hour - 8) / (17 - 8), 0), 1);
  const elapsed = (now.getDate() - 1) + dayFraction;
  return Math.min(elapsed / totalDays, 1);
}

function getTimeProgress(periodType) {
  if (periodType === "day") return getDayProgress();
  if (periodType === "week") return getWeekProgress();
  if (periodType === "month") return getMonthProgress();
  return null;
}

// --- Farvelogik baseret på afvigelse fra forventet ---
// deviation = (current - expected) / expected * 100
// Dag: ±15% = gul (dagsmål er svært at ramme præcist på minuttet)
// Uge/måned: ±10% = gul

function deviationColor(deviationPct, periodType) {
  const yellowBand = periodType === "day" ? 15 : 10;
  if (deviationPct >= yellowBand) return "#22C55E";     // grøn: foran
  if (deviationPct >= -yellowBand) return "#EAB308";   // gul: tæt på plan
  if (deviationPct >= -30) return "#F97316";            // orange: bagud men kan indhentes
  return "#EF4444";                                      // rød: tydeligt bagud
}

// Farve baseret på breakeven og mål — bruges til uge og måned
// Klare, levende farver der gradvist skifter mod grønt jo tættere på mål
function monthColor(current, breakEven, goal) {
  if (goal <= 0) return "#EF4444";
  const pct = (current / goal) * 100;

  if (pct >= 100) return "#16A34A";             // mål nået: mørk grøn
  if (pct >= 90)  return "#22C55E";             // 90–100%: klar grøn
  if (pct >= 75)  return "#84CC16";             // 75–90%: lys grøn

  // Under 75% — brug breakeven til at skelne gul/orange/rød
  if (current >= breakEven) return "#EAB308";   // over breakeven men under 75%: gul

  const gapPct = breakEven > 0 ? ((breakEven - current) / breakEven) * 100 : 100;
  if (gapPct <= 8)  return "#EAB308";           // tæt på breakeven: gul
  if (gapPct <= 25) return "#F97316";           // 8–25% under breakeven: orange
  return "#EF4444";                             // >25% under breakeven: rød
}

// --- Handlingstekst ---
// slotIndex = byens rang inden for sin kategori (0, 1, 2...)
// Garanterer at ingen to byer i samme kategori får samme tekst.
function pickVariant(options, slotIndex) {
  return options[slotIndex % options.length];
}

// Returnerer hvilken kategori-nøgle en by tilhører baseret på rawPct og periodType.
// Bruges til at beregne slotIndex i DepartmentGoals.
export function getCategoryKey(current, goal, periodType) {
  if (goal <= 0) return null;
  const rawPct = (current / goal) * 100;
  if (rawPct >= 100) return `${periodType}_100`;
  if (rawPct >= 75)  return `${periodType}_75`;
  if (rawPct >= 60)  return `${periodType}_60`;
  if (rawPct >= 30)  return `${periodType}_30`;
  return `${periodType}_0`;
}

function getActionText(current, _expected, goal, _breakEven, periodType, slotIndex) {
  if (goal <= 0) return null;
  const rawPct = (current / goal) * 100;

  if (periodType === "day") {
    if (rawPct >= 100) return pickVariant([
      "BOOM målet er nået – stærkt arbejde!",
      "Det her spiller – I er jo flyvende!",
      "Vanvittig dag – KÆMPE SEJR!",
    ], slotIndex);
    if (rawPct >= 75) return pickVariant([
      "FEDT I har ramt break even – nu jagter vi målet",
      "I er i målzonen – fortsæt sådan",
      "Sådan! Nu begynder det at blive sjovt",
    ], slotIndex);
    if (rawPct >= 60) return pickVariant([
      "Lige før break even – pres på nu",
      "Den er SÅ tæt på – giv den gas!",
      "Nu mangler der ikke meget – Lidt mere bundgas!",
    ], slotIndex);
    if (rawPct >= 30) return pickVariant([
      "Nu begynder det at ligne noget – fortsæt",
      "Godt i gang – der er mere i jer",
      "Tempoet er der – hold det",
    ], slotIndex);
    return pickVariant([
      "Okay start – nu bygger vi på",
      "I er i gang – lad os få tempo på",
      "Starten er sat – nu trykker vi til",
    ], slotIndex);
  }

  if (periodType === "week") {
    if (rawPct >= 100) return pickVariant([
      "Kæmpe uge – I leverer til UG",
      "Topniveau hele vejen",
      "Det her er en uge der spiller",
    ], slotIndex);
    if (rawPct >= 75) return pickVariant([
      "Lidt mere pedal – Så når I toppen denne uge!",
      "Stærk uge i gang",
      "Over breakeven – det er stærkt!",
    ], slotIndex);
    if (rawPct >= 60) return pickVariant([
      "Tæt på break even – nu gælder det",
      "Lige ved at knække ugen",
      "You don't win silver, you lose gold! Kom så!",
    ], slotIndex);
    if (rawPct >= 30) return pickVariant([
      "I følger planen – godt arbejde",
      "Stabil uge – hold tempoet",
      "Det ser fornuftigt ud – fortsæt",
    ], slotIndex);
    return pickVariant([
      "Ugen er ung – der er masser af muligheder",
      "Vi er i gang – byg på",
      "Starten er sat – nu skal der tryk på",
    ], slotIndex);
  }

  if (periodType === "month") {
    if (rawPct >= 100) return pickVariant([
      "Måneden er i mål – stærkt arbejde!!!",
      "I leverer på højt niveau, kæmpe inspiration",
      "Det her er en top måned – vær stolte af jer selv!",
    ], slotIndex);
    if (rawPct >= 75) return pickVariant([
      "I er på rette vej – fortsæt som I gør!",
      "Stærk måned i gang – I flyver mod toppen!",
      "Over breakeven – STÆRKT!",
    ], slotIndex);
    if (rawPct >= 60) return pickVariant([
      "Tæt på break even – SPEEDEREN I BUND",
      "Nu kan I næsten dufte sejren",
      "Nu mangler der ikke meget",
    ], slotIndex);
    if (rawPct >= 30) return pickVariant([
      "God udvikling – giv gas!",
      "Stabil måned indtil nu",
      "Lidt mere bundpedal – så når I den!",
    ], slotIndex);
    return pickVariant([
      "Måneden er i gang – lidt mere pedal",
      "Starten er sat – nu skal vi op i gear",
      "Der er masser af tid endnu, jeg tror på jer",
    ], slotIndex);
  }

  if (periodType === "last_month") {
    if (rawPct >= 100) return pickVariant([
      "Top måned – det her er niveauet vi elsker!",
      "I satte standarden – og I er FOR VILDE!",
      "Det var en vanvittig stærk måned!",
    ], slotIndex);
    if (rawPct >= 75) return pickVariant([
      "Stærk måned – sindsygt arbejde!",
      "I var tæt på toppen, fortsæt!",
      "Rigtig flot niveau – kæmpe indsats!",
    ], slotIndex);
    if (rawPct >= 60) return pickVariant([
      "Tæt på – næste gang tager I den!",
      "Lige ved og næsten – Come on, I kan godt",
      "You don't win silver – you lose gold",
    ], slotIndex);
    if (rawPct >= 30) return pickVariant([
      "Fin indsats men der er mere i jer",
      "I var med men ikke helt i mål, fokus",
      "Der er klart potentiale – lidt mere gas så er I der!",
    ], slotIndex);
    return pickVariant([
      "Det kan heldigvis kun gå en vej",
      "Ny måned, nye muligheder",
      "Vi løfter niveauet herfra",
    ], slotIndex);
  }

  // prev_month: samme som last_month
  if (periodType === "prev_month") {
    if (rawPct >= 100) return pickVariant([
      "Top måned – det her er niveauet vi elsker!",
      "I satte standarden – og I er FOR VILDE!",
      "Det var en vanvittig stærk måned!",
    ], slotIndex);
    if (rawPct >= 75) return pickVariant([
      "Stærk måned – sindsygt arbejde!",
      "I var tæt på toppen, fortsæt!",
      "Rigtig flot niveau – kæmpe indsats!",
    ], slotIndex);
    if (rawPct >= 60) return pickVariant([
      "Tæt på – næste gang tager I den!",
      "Lige ved og næsten – Come on, I kan godt",
      "You don't win silver – you lose gold",
    ], slotIndex);
    if (rawPct >= 30) return pickVariant([
      "Fin indsats men der er mere i jer",
      "I var med men ikke helt i mål, fokus",
      "Der er klart potentiale – lidt mere gas så er I der!",
    ], slotIndex);
    return pickVariant([
      "Det kan heldigvis kun gå en vej",
      "Ny måned, nye muligheder",
      "Vi løfter niveauet herfra",
    ], slotIndex);
  }

  return null;
}

function GoalBar({ label, current, breakEven, goal, periodType = "day", slotIndex = 0 }) {
  const rawPct = goal > 0 ? (current / goal) * 100 : 0;
  const pct = Math.min(rawPct, 100);
  const breakEvenPct = goal > 0 ? (breakEven / goal) * 100 : 0;
  const isGoalReached = current >= goal;

  const timeProgress = getTimeProgress(periodType);
  const expected = timeProgress !== null ? goal * timeProgress : null;
  const deviationPct = expected !== null && expected > 0
    ? ((current - expected) / expected) * 100
    : null;

  const barColor = monthColor(current, breakEven, goal);

  const actionText = getActionText(current, expected, goal, breakEven, periodType, slotIndex);

  return (
    <div className="mb-3">
      <div className="flex items-start justify-between mb-1 gap-2">
        <span className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide shrink-0">{label}</span>
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <span className="text-[13px] font-kpi text-foreground">{formatDKK(current)}</span>
          <span className="text-[13px] text-muted-foreground">/ {formatDKK(goal)}</span>
          <span className="text-[13px] font-bold ml-1" style={{ color: barColor }}>{Math.round(rawPct)}%</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="relative w-full h-4 bg-secondary rounded-full overflow-hidden"
        style={isGoalReached ? { boxShadow: `0 0 14px 3px ${barColor}60` } : {}}
      >
        {/* Breakeven marker */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-foreground/25 z-10"
          style={{ left: `${breakEvenPct}%` }}
        />
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            backgroundColor: barColor,
            boxShadow: isGoalReached ? `0 0 10px 2px ${barColor}` : "none",
          }}
        />
        {isGoalReached && (
          <div
            className="absolute inset-0 rounded-full animate-pulse-glow"
            style={{ backgroundColor: barColor, opacity: 0.15 }}
          />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-start justify-between mt-1 gap-2 flex-wrap">
        <span className="text-[13px] text-muted-foreground">
          Breakeven: {formatDKK(breakEven)}
        </span>
      </div>

      {/* Handlingstekst */}
      {actionText && (
        <p className="text-[12px] mt-1.5 leading-snug text-foreground">
          {actionText}
        </p>
      )}
    </div>
  );
}


function isDanishAfter13() {
  // Dansk tid = UTC+1 (vinter) / UTC+2 (sommer)
  const now = new Date();
  const danishHour = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Copenhagen" })).getHours();
  return danishHour >= 13;
}

export const DepartmentGoals = ({ departmentRanking, dayData, weekData, monthData, period = "day", streakByDept = {}, breakevenStreakByDept = {} }) => {
  if (!departmentRanking) return null;

  const isMonth = period === "month";
  const isLastMonth = period === "last_month";
  const isPrevMonth = period === "prev_month";
  const showMonthly = isMonth || isLastMonth || isPrevMonth;

  const isDay = period === "day";
  const isWeek = period === "week";
  const title = showMonthly ? "Månedlige Mål" : isDay ? "Daglige Mål" : isWeek ? "Ugentlige Mål" : "Daglige & Ugentlige Mål";
  const monthLabel = isLastMonth ? "Sidste måned" : isPrevMonth ? "Forrige måned" : "Denne måned";

  return (
    <Card className="bg-card border-border shadow-none animate-fade-in-up opacity-0 stagger-2">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[#FF2A6D]" />
          <CardTitle className="text-sm font-bold tracking-tight">
            {title}
          </CardTitle>
        </div>
        <p className="text-[13px] text-muted-foreground mt-1">
          {getSeason() === "high" ? "Høj sæson" : "Lav sæson"} · Farver baseret på forventet progression · Hvid streg = break even
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {(() => {
            // Beregn slotIndex per dept per periodType — garanterer unikke tekster
            // inden for samme kategori uanset antal byer i samme bånd.
            const activePeriod = showMonthly
              ? (isLastMonth ? "last_month" : isPrevMonth ? "prev_month" : "month")
              : isWeek ? "week" : "day";

            const todayWd = getTodayWeekday();
            const categoryCount = {};
            const slotIndexMap = {};
            for (const dk of ALL_DEPTS) {
              // Uge/måned bruger gennemsnit af hverdage — dagvisning bruger dagens ugedag
              const gDay = isDay ? getGoalDay(dk, todayWd) : getGoalDay(dk, 1); // man som fallback
              const avgGoal = getAvgWeekday(dk);
              const avgGoalDay = getSeason() === "high" ? avgGoal[2] : avgGoal[1];
              const gWeek = avgGoalDay * WORK_DAYS_PER_WEEK;
              const gMonth = avgGoalDay * WORK_DAYS_PER_MONTH;
              const amt = showMonthly
                ? (monthData?.[dk] || 0)
                : isWeek ? (weekData?.[dk] || 0) : (dayData?.[dk] || 0);
              const g = showMonthly ? gMonth : isWeek ? gWeek : gDay;
              const cat = getCategoryKey(amt, g, activePeriod);
              categoryCount[cat] = (categoryCount[cat] || 0);
              slotIndexMap[dk] = categoryCount[cat];
              categoryCount[cat]++;
            }

            return ALL_DEPTS.map((deptKey) => {
            // Dag-visning: brug dagens ugedag. Uge/måned: gennemsnit af hverdage.
            const avgData = getAvgWeekday(deptKey);
            const avgBreakEven = avgData[0];
            const avgGoalVal = getSeason() === "high" ? avgData[2] : avgData[1];
            const breakEvenDay = isDay ? getBreakEvenDay(deptKey, todayWd) : avgBreakEven;
            const goalDay = isDay ? getGoalDay(deptKey, todayWd) : avgGoalVal;
            const breakEvenWeek = avgBreakEven * WORK_DAYS_PER_WEEK;
            const goalWeek = avgGoalVal * WORK_DAYS_PER_WEEK;
            const breakEvenMonth = avgBreakEven * WORK_DAYS_PER_MONTH;
            const goalMonth = avgGoalVal * WORK_DAYS_PER_MONTH;

            const dayRaw = dayData?.[deptKey];
            const weekRaw = weekData?.[deptKey];
            const monthRaw = monthData?.[deptKey];

            // "ingen data" hvis afdelingen ikke har nogen omsætning i perioden
            const dayNoData = !dayRaw;
            const weekNoData = !weekRaw;
            const monthNoData = !monthRaw;

            const dayAmount = dayRaw || 0;
            const weekAmount = weekRaw || 0;
            const monthAmount = monthRaw || 0;

            const currentNoData = showMonthly ? monthNoData : isWeek ? weekNoData : dayNoData;
            const currentAmount = showMonthly ? monthAmount : isWeek ? weekAmount : dayAmount;
            const currentGoal = showMonthly ? goalMonth : isWeek ? goalWeek : goalDay;
            const goalReached = !currentNoData && currentAmount >= currentGoal;
            const slotIndex = slotIndexMap[deptKey] ?? 0;

            return (
              <div
                key={deptKey}
                className={`rounded-xl bg-secondary/50 p-4 transition-all duration-300 ${goalReached ? "goal-reached-card" : "border border-border"}`}
              >
                <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-1.5 flex-wrap">
                  {displayName(deptKey)}
                  {goalReached && (
                    <>
                      <Sparkles className="w-3.5 h-3.5 text-amber-400 animate-pulse-glow" style={{ animationDelay: "0s" }} />
                      <Sparkles className="w-3 h-3 text-amber-300 animate-pulse-glow" style={{ animationDelay: "0.3s" }} />
                      <Sparkles className="w-2.5 h-2.5 text-yellow-300 animate-pulse-glow" style={{ animationDelay: "0.6s" }} />
                    </>
                  )}
                  {isDay && streakByDept[deptKey] >= 2 && (
                    <span className="ml-auto text-[12px] font-bold text-orange-600 dark:text-orange-400 flex items-center gap-1">
                      🔥 Mål streak: {streakByDept[deptKey]} dage
                    </span>
                  )}
                  {isDay && breakevenStreakByDept[deptKey] >= 2 && (
                    <span className={`${streakByDept[deptKey] >= 2 ? "" : "ml-auto"} text-[12px] font-bold text-green-600 dark:text-green-400 flex items-center gap-1`}>
                      ✅ BE streak: {breakevenStreakByDept[deptKey]} dage
                    </span>
                  )}
                  {isDay && streakByDept[deptKey] >= 2 && isDanishAfter13() && dayData?.[deptKey] / getGoalDay(deptKey, todayWd) < 0.75 && (
                    <span className="w-full text-[13px] font-bold text-red-600 dark:text-red-400 mt-0.5">
                      ⚠️ Kæmp for at beholde jeres streak!
                    </span>
                  )}
                </h3>
                {showMonthly ? (
                  monthNoData ? (
                    <p className="text-sm text-muted-foreground italic">Ingen data</p>
                  ) : (
                    <GoalBar
                      label={monthLabel}
                      current={monthAmount}
                      breakEven={breakEvenMonth}
                      goal={goalMonth}
                      periodType={isLastMonth ? "last_month" : isPrevMonth ? "prev_month" : "month"}
                      slotIndex={slotIndex}
                    />
                  )
                ) : (
                  <>
                    {!isWeek && (
                      dayNoData ? (
                        <p className="text-sm text-muted-foreground italic">Ingen data i dag</p>
                      ) : (
                        <GoalBar
                          label="I dag"
                          current={dayAmount}
                          breakEven={breakEvenDay}
                          goal={goalDay}
                          periodType="day"
                          slotIndex={slotIndex}
                        />
                      )
                    )}
                    {!isDay && (
                      weekNoData ? (
                        <p className="text-sm text-muted-foreground italic">Ingen data denne uge</p>
                      ) : (
                        <GoalBar
                          label="Denne uge"
                          current={weekAmount}
                          breakEven={breakEvenWeek}
                          goal={goalWeek}
                          periodType="week"
                          slotIndex={slotIndex}
                        />
                      )
                    )}
                  </>
                )}
              </div>
            );
            });
          })()}

        </div>
      </CardContent>
    </Card>
  );
};
